import { Fragment, type ReactNode } from 'react';
import { cn } from '@/core/ui';
import { ArrowSquareOut, Info, LinkSimple, Sparkle } from '@/core/ui/icons';
import { isSafeHref, parseRichText, type RichNode } from '../../domain/richtext';
import type { BotMessage } from '../../domain/types';

/** Texto com **negrito**, *itálico*, [link](url) e quebras — sem HTML cru. */
export function RichText({ text }: { text: string }) {
  return <>{render(parseRichText(text))}</>;
}

function render(nodes: RichNode[]): ReactNode[] {
  return nodes.map((n, i) => {
    switch (n.type) {
      case 'text':
        return <Fragment key={i}>{n.text}</Fragment>;
      case 'break':
        return <br key={i} />;
      case 'bold':
        return <strong key={i}>{render(n.children)}</strong>;
      case 'italic':
        return <em key={i}>{render(n.children)}</em>;
      case 'link':
        return (
          <a
            key={i}
            href={n.href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
          >
            {n.text}
          </a>
        );
    }
  });
}

const bubble = 'max-w-[85%] rounded-2xl rounded-tl-md bg-surface-2 px-3.5 py-2.5 text-sm text-fg';

/** Uma mensagem da Centuria, como o portal mostraria. */
export function BotBubble({ message }: { message: Exclude<BotMessage, { type: 'pause' }> }) {
  switch (message.type) {
    case 'text':
      return (
        <div className={cn(bubble, 'break-words whitespace-normal')}>
          <RichText text={message.text} />
        </div>
      );
    case 'image':
      return isSafeHref(message.url) ? (
        <img
          src={message.url}
          alt={message.alt}
          loading="lazy"
          className="max-h-64 max-w-[85%] rounded-2xl object-cover"
        />
      ) : (
        <div className={bubble}>{message.alt || 'Imagem'}</div>
      );
    case 'video':
      return message.embed_url && /^https:\/\//i.test(message.embed_url) ? (
        <iframe
          src={message.embed_url}
          title="Vídeo"
          loading="lazy"
          allow="encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className="aspect-video w-[85%] rounded-2xl bg-black"
        />
      ) : /^https:\/\//i.test(message.url) ? (
        <video src={message.url} controls preload="metadata" className="w-[85%] rounded-2xl bg-black" />
      ) : (
        <div className={bubble}>Vídeo indisponível</div>
      );
    case 'link':
      return (
        <LinkCard
          label={message.label}
          url={message.url}
          external={message.external}
          caption={message.caption}
        />
      );
    case 'notice':
      return (
        <p className="mx-auto flex max-w-[90%] items-start gap-1.5 rounded-xl px-3 py-1.5 text-center text-xs text-fg-subtle">
          <Info aria-hidden className="mt-px size-3.5 shrink-0" />
          {message.text}
        </p>
      );
  }
}

function LinkCard({
  label,
  url,
  external,
  caption,
}: {
  label: string;
  url: string;
  external: boolean;
  caption: string;
}) {
  const body = (
    <>
      <span className="flex items-center gap-2 font-medium text-nav-active-fg">
        {external ? (
          <ArrowSquareOut aria-hidden className="size-4" />
        ) : (
          <LinkSimple aria-hidden className="size-4" />
        )}
        {label || url}
      </span>
      {caption && <span className="mt-0.5 block text-xs text-fg-subtle">{caption}</span>}
      {!external && <span className="mt-0.5 block text-xs text-fg-subtle">Abre no portal: {url}</span>}
    </>
  );
  const cls = 'block max-w-[85%] rounded-2xl bg-nav-active px-3.5 py-2.5 text-sm';
  return external && isSafeHref(url) ? (
    <a href={url} target="_blank" rel="noopener noreferrer" className={cn(cls, 'hover:underline')}>
      {body}
    </a>
  ) : (
    <div className={cls}>{body}</div>
  );
}

export function UserBubble({ text }: { text: string }) {
  return (
    <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-tr-md bg-brand-400 px-3.5 py-2.5 text-sm break-words whitespace-pre-wrap text-on-brand">
      {text}
    </div>
  );
}

/** "Digitando…" — três pontos que pulam (parados com movimento reduzido). */
export function Typing() {
  return (
    <div
      role="status"
      aria-label="A Centuria está digitando"
      className={cn(bubble, 'flex w-fit items-center gap-1 py-3')}
    >
      {[0, 150, 300].map((d) => (
        <span
          key={d}
          style={{ animationDelay: `${d}ms` }}
          className="size-1.5 rounded-full bg-fg-subtle motion-safe:animate-bounce"
        />
      ))}
    </div>
  );
}

export function Avatar() {
  return (
    <span
      aria-hidden
      className="flex size-7 shrink-0 items-center justify-center rounded-full bg-nav-active text-nav-active-fg"
    >
      <Sparkle className="size-4" />
    </span>
  );
}
